import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Search, HelpCircle, Leaf, Package, Users, Phone } from 'lucide-react';
import './faq.css';

const Faq = () => {
    const [activeAccordion, setActiveAccordion] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategory, setActiveCategory] = useState('all');

    const faqData = [
        // ABOUT COMPANY
        {
            id: 1,
            category: "company",
            question: "What is Shivneri Fresh?",
            answer:
                "Shivneri Fresh is a premium Indian grocery store located in Vashi, Navi Mumbai, established in September 2023. We focus on providing high-quality ingredients for home cooking."
        },
        {
            id: 2,
            category: "company",
            question: "What makes Shivneri Fresh unique?",
            answer:
                "We handpick all our products to ensure freshness, authenticity, and premium quality for our customers."
        },
        {
            id: 3,
            category: "company",
            question: "Where is Shivneri Fresh located?",
            answer:
                "Shivneri Fresh is located in Vashi, Navi Mumbai, serving the local community with fresh groceries and premium food essentials."
        },

        // PRODUCTS
        {
            id: 4,
            category: "products",
            question: "What products do you offer?",
            answer:
                "We offer sauces, fruit syrups, olive oil, chilli sauces, fruit juices, butter, vegetable soups, soya chaap, instant soups, and other premium groceries."
        },
        {
            id: 5,
            category: "products",
            question: "Are your products fresh and high quality?",
            answer:
                "Yes. All items at Shivneri Fresh are carefully selected for quality, freshness, and reliability."
        },
        {
            id: 6,
            category: "products",
            question: "Do you offer vegan or vegetarian products?",
            answer:
                "Yes. Many items including syrups, oils, juices, soups, and plant-based soya chaap are fully vegetarian and vegan-friendly."
        },

        // SUSTAINABILITY
        {
            id: 7,
            category: "sustainability",
            question: "Do you focus on sustainability?",
            answer:
                "We prioritize brands and suppliers that follow hygienic, safe, and sustainable food production practices."
        },
        {
            id: 8,
            category: "sustainability",
            question: "Are your products sourced responsibly?",
            answer:
                "Yes. We work with trusted suppliers who ensure safe, high-quality, and responsibly processed products."
        },

        // ORDERING
        {
            id: 9,
            category: "ordering",
            question: "Do you offer home delivery?",
            answer:
                "Yes, we provide home delivery in Vashi and nearby areas. Delivery options may vary depending on order value and location."
        },
        {
            id: 10,
            category: "ordering",
            question: "Can I place bulk or wholesale orders?",
            answer:
                "Yes. Bulk purchases for syrups, sauces, oils, and other items are available on request."
        },

        // SUPPORT
        {
            id: 11,
            category: "support",
            question: "How can I contact customer support?",
            answer:
                "You can reach us through phone, WhatsApp, or by visiting the store directly."
        },
        {
            id: 12,
            category: "support",
            question: "Do you accept returns or exchanges?",
            answer:
                "Yes. Unopened products in their original condition can be returned or exchanged according to our store policy."
        },

        // PARTNERSHIPS
        {
            id: 13,
            category: "partnerships",
            question: "Do you collaborate with other businesses?",
            answer:
                "Yes! Shivneri Fresh is open to partnerships with restaurants, cafes, and food businesses requiring quality ingredients."
        },
        {
            id: 14,
            category: "partnerships",
            question: "Do you supply products for events or catering?",
            answer:
                "Yes. We provide sauces, juices, syrups, and more in bulk for small events, catering, and functions."
        }
    ];


    const categories = [
        { id: 'all', name: 'All Questions', icon: HelpCircle, color: '#7B68EE' },
        { id: 'company', name: 'About Company', icon: Users, color: '#20B2AA' },
        { id: 'products', name: 'Products', icon: Package, color: '#FFD700' },
        { id: 'sustainability', name: 'Sustainability', icon: Leaf, color: '#98FB98' },
        { id: 'ordering', name: 'Ordering', icon: Package, color: '#FF6B6B' },
        { id: 'support', name: 'Support', icon: Phone, color: '#4ECDC4' },
        { id: 'partnerships', name: 'Partnerships', icon: Users, color: '#45B7D1' }
    ];

    const filteredFAQs = faqData.filter(faq => {
        const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
        const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    const toggleAccordion = (id) => {
        setActiveAccordion(activeAccordion === id ? null : id);
    };

    return (
        <div className="faq-page">
            <div className="container">
                <div className="gridd">
                    {/* Sidebar Categories */}
                    <div className="sidebar-wrap">
                        <div className="sidebar-card">
                            <h3 className="sidebar-title">Categories</h3>
                            <div className="category-stack">
                                {categories.map((category) => {
                                    const IconComponent = category.icon;
                                    const isActive = activeCategory === category.id;

                                    return (
                                        <button
                                            key={category.id}
                                            onClick={() => setActiveCategory(category.id)}
                                            className={`category-btn ${isActive ? 'active' : ''}`}
                                            style={isActive ? { '--cat-gradient-from': '#294c9d', '--cat-gradient-to': '#7786c4' } : {}}
                                        >
                                            <div className={`category-icon-wrap ${isActive ? 'active-icon-bg' : ''}`}>
                                                <IconComponent className={`category-icon ${isActive ? 'category-icon-active' : ''}`} />
                                            </div>
                                            <span className="category-name">{category.name}</span>
                                            <div className="category-count-wrap">
                                                <span className={`category-count ${isActive ? 'category-count-active' : ''}`}>
                                                    {category.id === 'all' ? faqData.length : faqData.filter(faq => faq.category === category.id).length}
                                                </span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* FAQ Content */}
                    <div className="main-wrap">
                        {filteredFAQs.length === 0 ? (
                            <div className="no-results">
                                <div className="no-results-icon">
                                    <Search className="no-icon-inner" />
                                </div>
                                <h3 className="no-results-title">No results found</h3>
                                <p className="no-results-sub">Try adjusting your search terms or select a different category.</p>
                            </div>
                        ) : (
                            <div className="faq-list-wrap">
                                <div className="list-header">
                                    <h2 className="list-title">
                                        {activeCategory === 'all' ? 'All Questions' : categories.find(cat => cat.id === activeCategory)?.name}
                                    </h2>
                                    <p className="list-count">{filteredFAQs.length} question{filteredFAQs.length !== 1 ? 's' : ''} found</p>
                                </div>

                                <div className="faq-cards">
                                    {filteredFAQs.map((faq, index) => (
                                        <div
                                            key={faq.id}
                                            className="faq-card"
                                            style={{
                                                animationDelay: `${index * 100}ms`,
                                                animation: 'fadeInUp 0.6s ease-out forwards'
                                            }}
                                        >
                                            <button
                                                onClick={() => toggleAccordion(faq.id)}
                                                className="faq-toggle"
                                            >
                                                <div className="faq-left">
                                                    <div className="bullet-gradient"></div>
                                                    <h3 className="faq-question">{faq.question}</h3>
                                                </div>

                                                <div className="faq-toggle-icon">
                                                    {activeAccordion === faq.id ? (
                                                        <ChevronUp className="toggle-icon" />
                                                    ) : (
                                                        <ChevronDown className="toggle-icon" />
                                                    )}
                                                </div>
                                            </button>

                                            <div className={`faq-body ${activeAccordion === faq.id ? 'open' : ''}`}>
                                                <div className="faq-body-inner">
                                                    <div className="faq-body-border">
                                                        <p className="faq-answer">{faq.answer}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

        </div>
    );
};

export default Faq;
